import queryString from 'query-string';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Divider } from '@xingternal/divider';
import { space4XL, spaceL } from '@xingternal/tokens';
import { Hero } from '@xingternal/typography';

import { DirectoryList, DirectorySection } from '@/components/_common/directory';
import { isOthersRoute } from '@/components/_common/directory/';
import { routes, serpsRoutes } from '@/config';

import Companies from './data/companies.json';

const StyledHero = styled(Hero)`
  text-transform: uppercase;
  margin-top: ${space4XL};
  margin-bottom: ${spaceL};
`;

type Props = {
  letter: string;
};

type CompanyData = { name: string; slug: string };

function getLinkWithTracking(companyName: string): string {
  const path = generatePath(serpsRoutes.company, { categoryValue: companyName });
  const tracking = queryString.stringify({
    sc_o: 'cd_company_name_click',
    sc_o_PropActionOrigin: 'company_directory',
  });

  return `${path}?${tracking}`;
}

function beginsWithAZ(value: string): boolean {
  return /^[a-z]/i.test(value);
}

function beginsWith(letter: string, value: string): boolean {
  return new RegExp('^' + letter, 'i').test(value);
}

export const CompaniesDirectory: React.FC<Props> = ({ letter }) => {
  const isLetterOthersRoute = isOthersRoute(letter);

  const companies = Object.keys(Companies).reduce<CompanyData[]>((acc, next) => {
    const companyName = Companies[next];

    if (
      (isLetterOthersRoute && !beginsWithAZ(companyName)) ||
      (!isLetterOthersRoute && beginsWith(letter, companyName))
    ) {
      acc.push({ name: companyName, slug: next });
    }

    return acc;
  }, []);

  return (
    <>
      <DirectoryList
        titleTranslationKey="COMPANY_DIRECTORY_LIST_H"
        letter={letter}
        route={routes.companiesDirectory}
        allowOthersRoute
      />
      <StyledHero forwardedAs="h2" size="large">
        {isLetterOthersRoute ? <FormattedMessage id="COMPANY_DIRECTORY_OTHERS" /> : letter}
      </StyledHero>
      <Divider />
      <DirectorySection
        titleTranslationKey="COMPANY_DIRECTORY_SECTION_H"
        list={companies}
        generateLinkProps={(item: CompanyData) => {
          return { to: getLinkWithTracking(item.slug), copy: item.name };
        }}
      />
    </>
  );
};
